import React, { useState } from "react";
import FormInput from "../Inputs/FormInput";
import { COMPOSE_EMAIL } from "./mailServices";
import Modal1 from "../Modal/Modal1";
import Lottie from "lottie-react";
import greenTick from "./Lotties/lottie-success.json";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { RingLoader } from "react-spinners";

const GetInTouch = () => {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [values, setValues] = useState({
    fullname: "",
    email: "",
    phone_number: "",
    message: "",
  });

  const handleOnChange = (e) => {
    const { value, id } = e.target;
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    const { fullname, email, phone_number, message } = values;

    if (!fullname || !email || !phone_number || !message) {
      toast.warn("Please fill all information");
      return;
    }

    setProcessing(true);

    const response = await COMPOSE_EMAIL({
      to: "consult@scaftechservices.com",
      message: `${message} \n Details: \r  Name: ${fullname} \r email: ${email} \r Mobile: ${phone_number}`,
      subject: `Contact Mail from: ${fullname} - ${email}`,
    });

    setProcessing(false);

    if (response?.isOk) {
      setSuccess(true);
      navigate(0);
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="contact-us"
      className="relative overflow-hidden bg-[#020819] py-16"
    >
      {/* background accents */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-56 w-56 rounded-full bg-[#00ad0f]/18 blur-3xl" />
        <div className="absolute right-[-40px] bottom-[-40px] h-64 w-64 rounded-full bg-[#062b6c]/35 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#101827_1px,transparent_1px),linear-gradient(to_bottom,#101827_1px,transparent_1px)] [background-size:48px_48px]" />
      </div>

      <div className="relative mx-auto w-11/12 max-w-6xl">
        {/* heading block */}
        <div className="mb-10 flex flex-col gap-3 text-center md:text-left">
          <span className="inline-flex items-center gap-2 self-center rounded-full border border-[#00ad0f]/40 bg-[#062b6c]/40 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.26em] text-[#e4ffe9] md:self-start">
            <span className="h-2 w-2 rounded-full bg-[#00ad0f] shadow-[0_0_10px_rgba(0,173,15,0.9)]" />
            Contact Scaftech
          </span>
          <div className="flex items-center justify-center gap-3 md:justify-start">
            <span className="h-10 w-[3px] rounded-full bg-[#062b6c]" />
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              Get In Touch
            </h2>
          </div>
          <p className="mx-auto max-w-2xl text-sm leading-relaxed text-slate-300 md:mx-0 md:text-base">
            Talk to our team about engineering services, inspections, lifting
            operations, or training programmes. We’ll get back to you as soon as
            possible.
          </p>
        </div>

        {/* content grid */}
        <div className="grid gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)]">
          {/* left: contact info */}
          <div className="flex flex-col gap-6 rounded-3xl border border-white/10 bg-[#050b18]/95 p-6 text-slate-100 shadow-[0_24px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ec3ff]">
                Direct contact
              </p>
              <p className="mt-2 text-lg font-semibold text-white">
                Let’s talk about your next project.
              </p>
            </div>

            <div className="space-y-5 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#e4ffe9]">
                  Mail
                </p>
                <a
                  className="mt-1 inline-block text-[15px] text-slate-200 hover:text-[#7fffb0]"
                  href="mailto:consult@scaftechservices.com"
                >
                  consult@scaftechservices.com
                </a>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#e4ffe9]">
                  Phone
                </p>
                <a
                  className="mt-1 inline-block text-[15px] text-slate-200 hover:text-[#7fffb0]"
                  href="tel:+2348161637797"
                >
                  +234 816 163 7797
                </a>
              </div>

              <div className="mt-3 space-y-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
                <p>Response window: 24–48 business hours</p>
                <p>Office hours: Mon – Fri, 8:00am – 5:00pm (WAT)</p>
              </div>
            </div>

            <div className="mt-2 rounded-2xl bg-[#062b6c]/30 px-4 py-3 text-xs text-slate-100">
              Prefer WhatsApp? Send us a direct message from the website hero or
              footer call-to-action.
            </div>
          </div>

          {/* right: form */}
          <div className="rounded-3xl border border-white/10 bg-[#050b18]/95 p-6 shadow-[0_24px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl">
            <form
              className="flex w-full flex-col gap-5"
              onSubmit={sendEmail}
              noValidate
            >
              <div className="flex flex-col gap-4 md:flex-row">
                <FormInput
                  className="leading-normal flex-1"
                  title={"Fullname"}
                  placeholder={"Full Name"}
                  id={"fullname"}
                  value={values.fullname}
                  type={"text"}
                  onChange={handleOnChange}
                />
                <FormInput
                  className="leading-normal flex-1"
                  title={"Email Address"}
                  placeholder={"Email Address"}
                  id={"email"}
                  type={"email"}
                  value={values.email}
                  onChange={handleOnChange}
                />
              </div>

              <FormInput
                title={"Phone Number"}
                placeholder={"Phone Number"}
                id={"phone_number"}
                type={"text"}
                value={values.phone_number}
                onChange={handleOnChange}
                className="leading-normal"
              />

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="message"
                  className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  placeholder="Tell us a bit about your request, project, or training need..."
                  value={values.message}
                  onChange={handleOnChange}
                  className="min-h-[130px] w-full rounded-2xl border border-white/15 bg-transparent px-3 py-3 text-sm font-medium text-slate-100 outline-none transition focus:border-[#00ad0f] focus:ring-1 focus:ring-[#00ad0f]"
                />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={processing}
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#2b9768] px-6 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-[#00ad0f] disabled:cursor-not-allowed disabled:opacity-70 md:w-auto"
                >
                  {processing ? (
                    <RingLoader color="#ffffff" size={20} />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Modal1 visible={success} onClose={() => setSuccess(false)}>
        <div className="flex min-h-[260px] flex-col items-center justify-center">
          <Lottie className="w-[90px]" animationData={greenTick} />
          <p className="mt-4 text-2xl font-bold text-[#062b6c]">
            Message sent
          </p>
          <p className="mt-1 px-6 text-center text-sm text-slate-600">
            Your message was sent successfully. Our team will get back to you
            shortly.
          </p>
        </div>
      </Modal1>

      <ToastContainer />
    </section>
  );
};

export default GetInTouch;
